import { withTranslation } from 'react-i18next';

import Loading from '../../../../components/Loading';
import MetricCards from '../../GscTools/components/MetricCards';
import PeriodTrend from '../../GscTools/components/PeriodTrend';
import { comparability, metricsFor } from '../../GscTools/metrics';
import { formatPeriod } from '../../GscTools/period';
import { sourceLabel } from '../sources';

/**
 * Block 03's drilldown — one page's own daily history in the selected source, in both windows.
 *
 * The selected page is a chip rather than a mode the page drops into: it is visible, it names the
 * source it was read in, and removing it returns the report to all pages without touching
 * anything else the user chose.
 *
 * The cards are the sums of the days actually returned, so they and the lines under them are the
 * same figures; a day Search Console had nothing for is a gap in both rather than a zero in
 * either. They will not always match the page's row above, and that is the honest outcome — the
 * row is Search Console's aggregate for the window, these are the days it chose to return inside it.
 */
function PageTrend({
    t,
    scope,
    status,
    error,
    selectedKey,
    current,
    baseline,
    metric,
    onMetric,
    onClear,
}) {
    const comparison = comparability(current?.coverage, baseline?.coverage ?? null);
    const source = sourceLabel(t, scope.searchType);

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.discover_news.page_trend.title')}</p>
                    <p className="gsc-tool-sub">
                        {selectedKey
                            ? `${source} · ${formatPeriod(scope.current)}`
                            : t('tools.discover_news.page_trend.description')}
                    </p>

                    {!!selectedKey && (
                        <div className="gsc-tool-chips">
                            <span className="gsc-tool-chip">
                                <span>{selectedKey}</span>
                                <button
                                    type="button"
                                    aria-label={t('tools.discover_news.page_trend.clear')}
                                    title={t('tools.discover_news.page_trend.clear')}
                                    onClick={onClear}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="card-body">
                {!selectedKey && (
                    <div className="gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-area-chart" />
                        </span>
                        <h4>{t('tools.discover_news.page_trend.idle_title')}</h4>
                        <p>{t('tools.discover_news.page_trend.idle')}</p>
                    </div>
                )}

                {!!selectedKey && status === 'loading' && <Loading padding={20} />}

                {!!selectedKey && status === 'error' && (
                    <div className="gsc-tool-banner error">
                        {error ?? t('global.something_went_wrong')}
                    </div>
                )}

                {!!selectedKey && status === 'done' && (
                    <>
                        <MetricCards
                            metrics={metricsFor(scope.searchType)}
                            totals={current?.totals}
                            priorTotals={baseline?.totals}
                            comparison={comparison}
                            selectedMetric={metric}
                            onSelectMetric={onMetric}
                            noteFor={(name, defaultNote) =>
                                current?.coverage
                                    ? t('tools.discover_news.page_trend.sum_note', {
                                          days: current.coverage.observed.length,
                                      })
                                    : defaultNote
                            }
                        />

                        <p className="gsc-tool-hint">
                            {t('tools.discover_news.page_trend.pick_card')}
                        </p>

                        <PeriodTrend
                            metric={metric}
                            current={current}
                            baseline={baseline}
                            fileName={t('tools.discover_news.page_trend.title')}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(PageTrend);
